package link.erpango.quip.features.garage

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.Dispatchers

import kotlinx.coroutines.withContext
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import link.erpango.quip.data.firebase.auth.FirebaseAuth
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.data.IDataSource
import link.erpango.quip.domain.entities.DUser
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.utils.AppDispatchers
import link.erpango.quip.domain.entities.VehicleType
import link.erpango.quip.domain.services.SignatureService
import link.erpango.quip.domain.usecases.IGarageUseCase
import link.erpango.quip.utils.PlatformUtils
import link.erpango.quip.utils.exportData
import link.erpango.quip.utils.FileUtils
import link.erpango.quip.utils.FileData
import link.erpango.quip.utils.DatetimeUtils
import link.erpango.quip.domain.entities.DVehicleDocument

import link.erpango.quip.ui.components.SyncStatus

data class VehicleDocumentUiModel(
    val document: DVehicleDocument,
    val status: SyncStatus
)

class GarageViewModel (
    private val mainInteractor: IGarageUseCase
): ViewModel() {
    private val _vehicles = MutableStateFlow<List<DVehicle>>(emptyList())
    private val _userData = MutableStateFlow<DUser?>(null)
    private val _distinctOwners = MutableStateFlow<List<String>>(emptyList())
    private val _catalogBrands = MutableStateFlow<List<String>>(emptyList())
    private val _catalogModels = MutableStateFlow<List<String>>(emptyList())
    val vehicles: StateFlow<List<DVehicle>> = _vehicles.asStateFlow()
    val userData: StateFlow<DUser?> = _userData.asStateFlow()
    val distinctOwners: StateFlow<List<String>> = _distinctOwners.asStateFlow()
    val catalogBrands: StateFlow<List<String>> = _catalogBrands.asStateFlow()
    val catalogModels: StateFlow<List<String>> = _catalogModels.asStateFlow()


    private val _documents = MutableStateFlow<List<VehicleDocumentUiModel>>(emptyList())
    val documents = _documents.asStateFlow()
    
    private val _isRefreshing = MutableStateFlow(false)
    val isRefreshing = _isRefreshing.asStateFlow()

    // Document action loading state (null = idle, non-null = loading message)
    private val _documentLoading = MutableStateFlow<String?>(null)
    val documentLoading = _documentLoading.asStateFlow()

    // Document action error state (null = no error)
    private val _documentError = MutableStateFlow<String?>(null)
    val documentError = _documentError.asStateFlow()

    fun clearDocumentError() { _documentError.value = null }

    init {
        viewModelScope.launch {
            val savedTheme = withContext(AppDispatchers.IO) {
                 mainInteractor.getSavedTheme()
            } ?: "Clean slate"
            
            println("GarageViewModel: Loaded saved theme: $savedTheme")
            
            val scheme = link.erpango.quip.theme.ColorSchemes.allSchemes[savedTheme]
            scheme?.let {
                link.erpango.quip.theme.QuipColors.updateScheme(it)
            }
        }

        viewModelScope.launch {
            mainInteractor.getGarage(viewModelScope.coroutineContext)
                .stateIn(
                    scope = viewModelScope,
                    started = SharingStarted.WhileSubscribed(),
                    initialValue = emptyList()
                ).collect {
                    _vehicles.value = it
                }
        }
        
        // Cargar catálogo de marcas
        loadCatalogBrands()

        viewModelScope.launch {
            _userData.value = mainInteractor.getLoggedUser()
            // Initial fetch if user is already available
            if (_userData.value != null) {
                fetchGarageFromServer()
            }
            
            mainInteractor.observeUserFlow(viewModelScope.coroutineContext)?.let {
                it.stateIn(
                    scope = viewModelScope,
                    started = SharingStarted.WhileSubscribed(),
                    initialValue = null
                ).collect { newUser ->
                    val previousUser = _userData.value
                    _userData.value = newUser
                    // Fetch garage when user changes (e.g. login)
                    if (newUser != null && newUser != previousUser) {
                        fetchGarageFromServer()
                    }
                }
            }
        }
        viewModelScope.launch(AppDispatchers.IO) {
            mainInteractor.getLoggedUser()?.let {
                mainInteractor.fetchMyGarage().let { response ->
                    if (response is DResponse.Success) {
                        val localGarage = mainInteractor.getFullGarage()
                        response.data.forEach { remoteVehicle ->
                            if (!localGarage.any { it.vin == remoteVehicle.vin }) {
                                mainInteractor.addVehicleToLocalGarage(
                                    vehicle = remoteVehicle
                                )
                            }
                        }
                    }
                }
            }
        }
        
        // Load distinct owners for autocomplete
        loadDistinctOwners()
    }
    
    private fun loadDistinctOwners() {
        viewModelScope.launch(AppDispatchers.IO) {
            _distinctOwners.value = mainInteractor.getDistinctOwners()
        }
    }
    
    fun refreshDistinctOwners() {
        loadDistinctOwners()
    }

    /**
     * Firma y crea el vehículo, e informa del resultado: a diferencia de [addVehicle], el
     * formulario puede avisar si el VIN ya existe o si el backend ha fallado.
     */
    fun createVehicle(vehicle: DVehicle, onResult: (DResponse<DVehicle>) -> Unit) {
        viewModelScope.launch {
            val signed = vehicle.copy(signature = SignatureService.signVehicle(vehicle))
            val result = withContext(AppDispatchers.IO) { mainInteractor.createVehicle(signed) }
            onResult(result)
        }
    }

    fun addVehicle(vehicle: DVehicle) {
        addVehicle(
            vin = vehicle.vin,
            plate = vehicle.plate,
            brand = vehicle.brand,
            model = vehicle.model,
            vehicleType = vehicle.vehicleType,
            year = vehicle.year.toLong(),
            color = vehicle.color,
            owner = vehicle.owner,
            engineOil = vehicle.engineOil,
            frontTireSize = vehicle.frontTireSize,
            rearTireSize = vehicle.rearTireSize,
            frontTirePressureBelow160 = vehicle.frontTirePressureBelow160,
            rearTirePressureBelow160 = vehicle.rearTirePressureBelow160,
            frontTirePressureAbove160 = vehicle.frontTirePressureAbove160,
            rearTirePressureAbove160 = vehicle.rearTirePressureAbove160,
            recommendedFuel = vehicle.recommendedFuel,
            engineDetails = vehicle.engineDetails,
            cylinderCount = vehicle.cylinderCount,
            nextRevisionDate = vehicle.nextRevisionDate,
            nextItvDate = vehicle.nextItvDate,
            firstRegistrationDate = vehicle.firstRegistrationDate
        )
    }

    fun addVehicle(
        vin: String,
        plate: String,
        brand: String,
        model: String,
        vehicleType: VehicleType,
        year: Long,
        color: String,
        owner: String,
        engineOil: String,
        frontTireSize: String,
        rearTireSize: String,
        frontTirePressureBelow160: Double,
        rearTirePressureBelow160: Double,
        frontTirePressureAbove160: Double,
        rearTirePressureAbove160: Double,
        recommendedFuel: String,
        engineDetails: String,
        cylinderCount: Int,
        nextRevisionDate: Long?,
        nextItvDate: Long?,
        firstRegistrationDate: Long?
    ) {
        viewModelScope.launch {
            if (mainInteractor.getFullGarage().any { it.vin == vin }) {
                return@launch
            }

        val vehicle = DVehicle(
            vin = vin,
            plate = plate,
            brand = brand,
            model = model,
            vehicleType = vehicleType,
            year = year.toInt(),
            color = color,
            owner = owner,
            engineOil = engineOil,
            frontTireSize = frontTireSize,
            rearTireSize = rearTireSize,
            frontTirePressureBelow160 = frontTirePressureBelow160,
            rearTirePressureBelow160 = rearTirePressureBelow160,
            frontTirePressureAbove160 = frontTirePressureAbove160,
            rearTirePressureAbove160 = rearTirePressureAbove160,
            recommendedFuel = recommendedFuel,
            engineDetails = engineDetails,
            cylinderCount = cylinderCount,
            nextRevisionDate = nextRevisionDate,
            nextItvDate = nextItvDate,
            firstRegistrationDate = firstRegistrationDate
        )
        val signature = SignatureService.signVehicle(vehicle)

            mainInteractor.createVehicle(
                vin = vin,
                plate = plate,
                brand = brand,
                model = model,
                vehicleType = vehicleType,
                year = year,
                color = color,
                owner = owner,
                engineOil = engineOil,
                frontTireSize = frontTireSize,
                rearTireSize = rearTireSize,
                frontTirePressureBelow160 = frontTirePressureBelow160,
                rearTirePressureBelow160 = rearTirePressureBelow160,
                frontTirePressureAbove160 = frontTirePressureAbove160,
                rearTirePressureAbove160 = rearTirePressureAbove160,
                recommendedFuel = recommendedFuel,
                engineDetails = engineDetails,
                cylinderCount = cylinderCount,
                signature = signature,
                nextRevisionDate = nextRevisionDate,
                nextItvDate = nextItvDate,
                firstRegistrationDate = firstRegistrationDate
            )
        }
    }

    fun updateVehicle(vehicle: DVehicle) {
        viewModelScope.launch {
            // Sign vehicle if needed (though vehicle signature is constant based on immutable fields)
            // But if we want to store it:
            val signature = SignatureService.signVehicle(vehicle)
            val signedVehicle = vehicle.copy(signature = signature)
            mainInteractor.updateVehicle(
                vin = signedVehicle.vin,
                plate = signedVehicle.plate,
                brand = signedVehicle.brand,
                model = signedVehicle.model,
                vehicleType = signedVehicle.vehicleType,
                year = signedVehicle.year.toLong(),
                color = signedVehicle.color,
                owner = signedVehicle.owner,
                engineOil = signedVehicle.engineOil,
                frontTireSize = signedVehicle.frontTireSize,
                rearTireSize = signedVehicle.rearTireSize,
                frontTirePressureBelow160 = signedVehicle.frontTirePressureBelow160,
                rearTirePressureBelow160 = signedVehicle.rearTirePressureBelow160,
                frontTirePressureAbove160 = signedVehicle.frontTirePressureAbove160,
                rearTirePressureAbove160 = signedVehicle.rearTirePressureAbove160,
                recommendedFuel = signedVehicle.recommendedFuel,
                engineDetails = signedVehicle.engineDetails,
                cylinderCount = signedVehicle.cylinderCount,
                frontBulbType = signedVehicle.frontBulbType,
                rearBulbType = signedVehicle.rearBulbType,
                batterySpecs = signedVehicle.batterySpecs,
                coolantType = signedVehicle.coolantType,
                signature = signedVehicle.signature,
                nextRevisionDate = signedVehicle.nextRevisionDate,
                nextItvDate = signedVehicle.nextItvDate,
                firstRegistrationDate = signedVehicle.firstRegistrationDate
            )
        }
    }

    fun signAllMaintenances(vehicleVin: String) {
        viewModelScope.launch {
            mainInteractor.signAllMaintenances(vehicleVin)
        }
    }

    fun getVehicleByVin(vin: String): DVehicle? {
        return _vehicles.value.find { it.vin == vin }
    }

    fun exportVehicle(vin: String) {
        vehicles.value.firstOrNull { it.vin == vin }?.let {
            exportData(listOf(it)).let {
                PlatformUtils.shareJsonData(it)
                println(it)
            }
        }
    }

    //FIXME -> moveremos a pantalla de login
    fun firebaseAuthAccessor(): FirebaseAuth = mainInteractor.getFirebaseAuthAccesor()

    suspend fun getCurrentUser() {
        mainInteractor.getFirebaseAuthAccesor().user?.getEmail()?.collect() {
            println("User: ${it.getOrNull()}")
        }
    }

    // FIXME esto no funciona
    fun signOut() {
        mainInteractor.getFirebaseAuthAccesor().signOut(viewModelScope)
    }

    fun fetchGarageFromServer() {
        viewModelScope.launch(AppDispatchers.IO) {
            _isRefreshing.value = true
            try {
                mainInteractor.fetchMyGarage().let { response ->
                    if (response is DResponse.Success) {
                        val localGarage = mainInteractor.getFullGarage()
                        val remoteVins = response.data.map { it.vin }.toSet()
                        
                        // Delete vehicles from local DB that are not in the backend
                        localGarage.forEach { localVehicle ->
                            if (!remoteVins.contains(localVehicle.vin)) {
                                mainInteractor.deleteVehicleLocal(localVehicle.vin)
                            }
                        }
                        
                        // Add or update vehicles from backend
                        response.data.forEach { remoteVehicle ->
                            if (!localGarage.any { it.vin == remoteVehicle.vin }) {
                                mainInteractor.addVehicleToLocalGarage(
                                    vehicle = remoteVehicle
                                )
                            }
                        }
                    }
                }
            } finally {
                _isRefreshing.value = false
            }
        }
    }

    fun refreshUser() {
        viewModelScope.launch {
            _userData.value = mainInteractor.getLoggedUser()
        }
    }

    // Catálogo de marcas y modelos
    fun loadCatalogBrands() {
        viewModelScope.launch(AppDispatchers.IO) {
            val dbBrands = mainInteractor.getCatalogBrands()
            _catalogBrands.value = dbBrands.ifEmpty {
                link.erpango.quip.QuipConstants.VehicleAttributes.VehicleBrands
            }
        }
    }

    fun loadCatalogModels(brandName: String) {
        viewModelScope.launch(AppDispatchers.IO) {
            val dbModels = mainInteractor.getCatalogModelsByBrand(brandName)
            _catalogModels.value = dbModels.ifEmpty {
                link.erpango.quip.QuipConstants.VehicleAttributes.VehicleModelsByBrand[brandName] ?: emptyList()
            }
        }
    }

    // Documents
    fun loadDocuments(vin: String) {
        viewModelScope.launch {
            _documentLoading.value = "Loading documents..."
            try {
                // 1. Get local documents
                val localDocs = mainInteractor.getDocuments(vin)
                
                // 2. Get remote metadata
                val response = mainInteractor.getVehicleDocumentsMetadata(vin)
                val remoteDocs = if (response is DResponse.Success) response.data else emptyList()
                
                // 3. Combine and determine status
                val combinedDocs = mutableListOf<VehicleDocumentUiModel>()
                
                // Process local docs
                localDocs.forEach { localDoc ->
                    val remoteDoc = remoteDocs.find { it.id == localDoc.id && it.id != 0L }
                    if (remoteDoc != null) {
                        combinedDocs.add(VehicleDocumentUiModel(localDoc, SyncStatus.SYNCED))
                    } else {
                        combinedDocs.add(VehicleDocumentUiModel(localDoc, SyncStatus.LOCAL_ONLY))
                    }
                }
                
                // Process remote only docs
                remoteDocs.forEach { remoteDoc ->
                    if (localDocs.none { it.id == remoteDoc.id }) {
                        combinedDocs.add(VehicleDocumentUiModel(remoteDoc, SyncStatus.REMOTE_ONLY))
                    }
                }
                
                _documents.value = combinedDocs
            } catch (e: Exception) {
                _documentError.value = "Error loading documents: ${e.message}"
            } finally {
                _documentLoading.value = null
            }
        }
    }

    fun syncDocuments(vin: String) {
       loadDocuments(vin)
    }

    fun downloadDocument(document: DVehicleDocument) {
        viewModelScope.launch {
            _documentLoading.value = "Downloading ${document.name}..."
            try {
                val response = mainInteractor.downloadDocument(document.id)
                if (response is DResponse.Success) {
                    val fullDoc = response.data
                    if (fullDoc.fileBase64 != null) {
                        val path = FileUtils.saveBase64ToFile(fullDoc.fileBase64, fullDoc.name)
                        if (path.isNotEmpty()) {
                            // Update local DB
                            mainInteractor.updateLocalDocumentPath(fullDoc, path)
                            loadDocuments(document.vehicleVin)
                        }
                    }
                } else {
                     _documentError.value = "Download failed: ${(response as? DResponse.Error)?.error}"
                }
            } catch (e: Exception) {
                 _documentError.value = "Download failed: ${e.message}"
            } finally {
                _documentLoading.value = null
            }
        }
    }

    fun uploadDocument(vin: String, fileData: FileData) {
        viewModelScope.launch {
            _documentLoading.value = "Uploading ${fileData.fileName}..."
            try {
                val path = FileUtils.saveBase64ToFile(fileData.fileContentBase64, fileData.fileName)
                
                val sizeBytes = (fileData.fileContentBase64.length * 3 / 4).toLong()
                val sizeMb = sizeBytes.toDouble() / (1024 * 1024)

                val doc = DVehicleDocument(
                    vehicleVin = vin,
                    name = fileData.fileName,
                    filePath = path,
                    fileType = fileData.mimeType,
                    createdAt = DatetimeUtils.current(),
                    fileContentBase64 = fileData.fileContentBase64,
                    sizeMb = sizeMb,
                    updatedAt = DatetimeUtils.current()
                )
                
                val response = mainInteractor.uploadDocument(doc)
                if (response is DResponse.Error) {
                    _documentError.value = "Upload failed: ${response.error}"
                }
                // Always reload to show local item (it was saved locally by Interactor before upload)
                loadDocuments(vin)
            } catch (e: Exception) {
                _documentError.value = "Upload failed: ${e.message}"
            } finally {
                _documentLoading.value = null
            }
        }
    }
    
    fun deleteDocument(document: DVehicleDocument) {
        viewModelScope.launch {
            _documentLoading.value = "Deleting ${document.name}..."
            try {
                val response = mainInteractor.deleteDocument(document)
                if (response is DResponse.Success) {
                    loadDocuments(document.vehicleVin)
                } else {
                    _documentError.value = "Delete failed: ${(response as? DResponse.Error)?.error ?: "Unknown error"}"
                }
            } catch (e: Exception) {
                _documentError.value = "Delete failed: ${e.message}"
            } finally {
                _documentLoading.value = null
            }
        }
    }

    fun renameDocument(document: DVehicleDocument, newName: String) {
        viewModelScope.launch {
            _documentLoading.value = "Renaming..."
            try {
                val response = mainInteractor.renameDocument(document, newName)
                if (response is DResponse.Success) {
                    loadDocuments(document.vehicleVin)
                } else {
                    _documentError.value = "Rename failed: ${(response as? DResponse.Error)?.error ?: "Unknown error"}"
                }
            } catch (e: Exception) {
                _documentError.value = "Rename failed: ${e.message}"
            } finally {
                _documentLoading.value = null
            }
        }
    }


    fun openDocument(document: DVehicleDocument) {
        viewModelScope.launch(Dispatchers.Default) {
            _documentLoading.value = "Opening ${document.name}..."
            try {
                // Check if local file exists first
                if (document.filePath.isNotEmpty() && FileUtils.fileExists(document.filePath)) {
                     withContext(Dispatchers.Main) {
                        PlatformUtils.openFile(document.filePath, document.fileType)
                    }
                    return@launch
                }

                var base64 = document.fileBase64
                
                if (base64.isNullOrEmpty()) {
                    val response = mainInteractor.getDocument(document.id)
                    if (response is DResponse.Success) {
                        base64 = response.data.fileBase64
                    }
                }

                if (base64 != null) {
                    val filePath = FileUtils.saveBase64ToFile(base64!!, document.name)
                    if (filePath.isNotEmpty()) {
                        // Update the local DB with the file path so next time we don't need to download it
                        mainInteractor.updateLocalDocumentPath(document, filePath)
                        
                        // Update the current state so the UI reflects the change immediately 
                        // and subsequent clicks use the updated document object
                        // We reload the list to update the status and path
                        loadDocuments(document.vehicleVin)

                        withContext(Dispatchers.Main) {
                            PlatformUtils.openFile(filePath, document.fileType)
                        }
                    }
                } else {
                    _documentError.value = "Could not load document content"
                }
            } catch (e: Exception) {
                _documentError.value = "Open failed: ${e.message}"
            } finally {
                _documentLoading.value = null
            }
        }
    }

    fun shareDocument(document: DVehicleDocument) {
        viewModelScope.launch(Dispatchers.Default) {
            _documentLoading.value = "Preparing ${document.name}..."
            try {
                // Check if local file exists first
                if (document.filePath.isNotEmpty() && FileUtils.fileExists(document.filePath)) {
                     withContext(Dispatchers.Main) {
                        PlatformUtils.shareFile(document.filePath, document.fileType)
                    }
                    return@launch
                }

                var base64 = document.fileBase64

                if (base64.isNullOrEmpty()) {
                    val response = mainInteractor.getDocument(document.id)
                    if (response is DResponse.Success) {
                        base64 = response.data.fileBase64
                    }
                }

                if (base64 != null) {
                    val filePath = FileUtils.saveBase64ToFile(base64!!, document.name)
                    if (filePath.isNotEmpty()) {
                        // Update the local DB with the file path so next time we don't need to download it
                        mainInteractor.updateLocalDocumentPath(document, filePath)
                        
                        // Update the current state so the UI reflects the change immediately 
                        // and subsequent clicks use the updated document object
                        loadDocuments(document.vehicleVin)

                        withContext(Dispatchers.Main) {
                            PlatformUtils.shareFile(filePath, document.fileType)
                        }
                    }
                } else {
                    _documentError.value = "Could not load document content"
                }
            } catch (e: Exception) {
                _documentError.value = "Share failed: ${e.message}"
            } finally {
                _documentLoading.value = null
            }
        }
    }
    
    fun ensureDocumentLocal(document: DVehicleDocument) {
        viewModelScope.launch(Dispatchers.Default) {
            // Check if local file exists first (and path is not empty)
            if (document.filePath.isNotEmpty() && FileUtils.fileExists(document.filePath)) {
                return@launch
            }

            try {
                var base64 = document.fileBase64
                if (base64.isNullOrEmpty()) {
                    val response = mainInteractor.getDocument(document.id)
                    if (response is DResponse.Success) {
                        base64 = response.data.fileBase64
                    }
                }

                if (base64 != null) {
                    val filePath = FileUtils.saveBase64ToFile(base64!!, document.name)
                    if (filePath.isNotEmpty()) {
                        mainInteractor.updateLocalDocumentPath(document, filePath)
                        loadDocuments(document.vehicleVin)
                    }
                }
            } catch (e: Exception) {
                println("Error ensuring local document: ${e.message}")
            }
        }
    }

    fun retryUploadDocument(document: DVehicleDocument) {
        viewModelScope.launch {
            _documentLoading.value = "Uploading ${document.name}..."
            try {
                // Ensure we have base64 content if not present on object but file exists
                val docToUpload = if (document.fileContentBase64.isNullOrEmpty() && document.filePath.isNotEmpty()) {
                     val base64 = FileUtils.readFileToBase64(document.filePath)
                     if (base64.isNotEmpty()) document.copy(fileContentBase64 = base64) else document
                } else document

                val response = mainInteractor.uploadDocument(docToUpload)
                if (response is DResponse.Error) {
                    _documentError.value = "Upload failed: ${response.error}"
                }
                loadDocuments(document.vehicleVin)
            } catch (e: Exception) {
                _documentError.value = "Upload failed: ${e.message}"
            } finally {
                _documentLoading.value = null
            }
        }
    }
}